import Link from 'next/link'
import { Mail, Linkedin } from 'lucide-react'
import { LogoMark } from './Logo'

function XLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.738l7.73-8.835L1.254 2.25H8.08l4.259 5.629L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  )
}

const COLS = [
  {
    label: 'Product',
    links: [
      // v112: the dedicated /modules page is killed; the homepage tells the
      // modules story once, so the footer no longer links to it.
      // v138: rebalanced. Product now owns the product surfaces (use cases,
      // integrations, changelog, roadmap, status); previously /integrations
      // and /roadmap had no inbound navigation link at all.
      { label: 'Pricing',      href: '/#pricing'      },
      { label: 'FAQs',         href: '/#faq'          },
      { label: 'Use cases',    href: '/use-cases'     },
      { label: 'Integrations', href: '/integrations'  },
      // Changelog de-linked pre-launch — the page is empty until the first
      // entries land after launch (the route itself stays in place).
      { label: 'Roadmap',      href: '/roadmap'       },
      { label: 'Status',       href: '/status'        },
      { label: 'Dashboard',    href: '/dashboard'     },
    ],
  },
  {
    label: 'Company',
    links: [
      { label: 'About',           href: '/about'           },
      { label: 'Blog',            href: '/blog'            },
      { label: 'Press',           href: '/press'           },
      { label: 'Help',            href: '/help'            },
      { label: 'Contact',         href: '/contact'         },
      { label: 'Security',        href: '/security'        },
      { label: 'Alternatives',    href: '/alternatives'    },
      { label: 'Code of conduct', href: '/code-of-conduct' },
    ],
  },
  {
    label: 'Legal',
    // v77: DPA + AUP added as full legal-tail. CCPA "Do Not Sell or Share"
    // is a US-state-law requirement (CPRA §1798.135); link must be visible
    // on every page. We deep-link to /privacy#ccpa-do-not-sell so the user
    // lands on the controlling notice; the anchor target is rendered by the
    // CCPA section of the privacy policy. aria-label spells out the link
    // intent because the visible text is intentionally short.
    links: [
      { label: 'Privacy',                                href: '/privacy'                          },
      { label: 'Terms',                                  href: '/terms'                            },
      { label: 'Cookies',                                href: '/privacy/cookies'                  },
      { label: 'Sub-processors',                         href: '/privacy/sub-processors'           },
      { label: 'DPA',                                    href: '/privacy/dpa',                     ariaLabel: 'Data Processing Addendum'         },
      { label: 'AUP',                                    href: '/privacy/aup',                     ariaLabel: 'Acceptable Use Policy'             },
      { label: 'Accessibility',                          href: '/accessibility'                    },
      { label: 'Responsible disclosure',                 href: '/security/responsible-disclosure'  },
      { label: 'Erasure request',                        href: '/privacy/erasure-request'          },
      { label: 'Refund policy',                          href: '/refund-policy'                    },
      { label: 'Anti-harassment policy',                 href: '/anti-harassment-policy'           },
      { label: 'Anti-spam policy',                       href: '/anti-spam-policy'                 },
      { label: 'Do Not Sell or Share My Personal Info',  href: '/privacy#ccpa-do-not-sell',        ariaLabel: 'California Do Not Sell or Share My Personal Information' },
    ],
  },
]

const SOCIAL = [
  { label: 'Email',    href: 'mailto:hello@pulsesignal.co',                     icon: Mail     },
  { label: 'X',        href: 'https://twitter.com/pulsesignalco',               icon: XLogo    },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/pulsesignalco/', icon: Linkedin },
]

// NB-F324: turn each footer column from a bare <ul> into a labelled
// <nav aria-labelledby="…"> region so assistive tech announces "Product
// navigation" / "Company navigation" / "Legal navigation" rather than
// three identical "list, ten items" announcements. Each heading is now a
// real <h2> (sr-only-tracking via id, visually unchanged eyebrow).
function colId(label: string): string {
  return `footer-col-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
}

export default function MarketingFooter() {
  const year = new Date().getFullYear()
  return (
    <footer
      role="contentinfo"
      aria-label="Site footer"
      className="border-t border-ink-900/10 mt-16 sm:mt-24"
    >
      <div className="editorial pt-14 sm:pt-20 pb-10">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="col-span-2 lg:col-span-5">
            <Link
              href="/"
              aria-label="PulseSignal home"
              className="flex items-center gap-2.5 group w-fit transition-transform duration-200 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100 rounded-lg"
            >
              <LogoMark size={56} />
              <span className="text-[26px] sm:text-[30px] font-semibold tracking-tight text-ink-900 group-hover:text-terra-500 transition-colors duration-200">
                PulseSignal
              </span>
            </Link>
            <p className="mt-5 text-body text-ink-700 max-w-md leading-relaxed">
              Competitive intelligence for SaaS. Public-source signal pipelines, refreshed
              every day. Delivered to your inbox, Slack, or Telegram.
            </p>
            <ul
              aria-label="Social and contact links"
              className="mt-7 flex items-center gap-3 list-none p-0"
            >
              {SOCIAL.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target={s.href.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="icon-bubble w-9 h-9 rounded-full border border-ink-900/15 hover:border-terra-500/60 flex items-center justify-center text-ink-700 hover:text-terra-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100"
                  >
                    <s.icon className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                </li>
              ))}
            </ul>
          </div>
          {COLS.map((col) => {
            const id = colId(col.label)
            return (
              <nav
                key={col.label}
                aria-labelledby={id}
                className="lg:col-span-2"
              >
                <h2
                  id={id}
                  className="text-eyebrow font-semibold tracking-widest uppercase text-ink-600 mb-4"
                >
                  {col.label}
                </h2>
                <ul className="space-y-2.5">
                  {col.links.map((l) => {
                    // Some legal links (DPA / AUP / "Do Not Sell") use a short
                    // visible label and supply an ariaLabel that spells out
                    // the full meaning for screen-reader users.
                    const ariaLabel = 'ariaLabel' in l ? (l as { ariaLabel?: string }).ariaLabel : undefined
                    return (
                      <li key={l.href}>
                        <Link
                          href={l.href}
                          aria-label={ariaLabel}
                          className="link-quiet no-underline text-body-sm text-ink-700 hover:text-ink-900 focus-visible:outline-none focus-visible:text-terra-600 focus-visible:underline underline-offset-4"
                        >
                          {l.label}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </nav>
            )
          })}
        </div>

        <div className="rule mt-14" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
          <p className="text-caption text-ink-600">
            <span aria-label={`Copyright ${year} PulseSignal`}>
              © {year} PulseSignal
            </span>{' '}
            · Built for operators who refuse to be late.
          </p>
        </div>
      </div>
    </footer>
  )
}
